import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExperimentStatusBadge } from "./experiment-status-badge";
import type { Experiment } from "@/types/experiment";
import { formatDate } from "@/lib/format";

interface ExperimentDetailDialogProps {
  experiment: Experiment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExperimentDetailDialog({
  experiment,
  open,
  onOpenChange,
}: ExperimentDetailDialogProps) {
  if (!experiment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {experiment.name}
            <ExperimentStatusBadge status={experiment.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div>
            <span className="text-muted-foreground">Key</span>
            <div>
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                {experiment.key}
              </code>
            </div>
          </div>

          {experiment.description && (
            <div>
              <span className="text-muted-foreground">설명</span>
              <p>{experiment.description}</p>
            </div>
          )}

          <div>
            <span className="text-muted-foreground">Variants</span>
            <div className="mt-1 space-y-1.5">
              {experiment.variants.map((v) => (
                <div
                  key={v.key}
                  className="flex items-center justify-between rounded border px-3 py-1.5"
                >
                  <code className="text-xs font-mono">{v.key}</code>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{ width: `${(v.weight / 10000) * 100}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-xs text-muted-foreground">
                      {(v.weight / 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {experiment.targeting && (
            <div>
              <span className="text-muted-foreground">Targeting</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {experiment.targeting.newUsersOnly && (
                  <Badge variant="secondary">신규 유저만</Badge>
                )}
                {experiment.targeting.platforms?.map((p) => (
                  <Badge key={p} variant="outline">
                    {p}
                  </Badge>
                ))}
                {experiment.targeting.startDate && (
                  <Badge variant="outline">
                    시작: {formatDate(experiment.targeting.startDate, { format: "short" })}
                  </Badge>
                )}
                {experiment.targeting.endDate && (
                  <Badge variant="outline">
                    종료: {formatDate(experiment.targeting.endDate, { format: "short" })}
                  </Badge>
                )}
                {/* TODO: 구독 상태별 필터 배지 표시 */}
                {/* TODO: 가입 기간별 필터 배지 표시 */}
                {/* TODO: 사용량 기반 필터 배지 표시 */}
              </div>
            </div>
          )}

          <div className="flex gap-6 text-xs text-muted-foreground pt-2 border-t">
            <span>생성: {formatDate(experiment.createdAt, { format: "short" })}</span>
            <span>수정: {formatDate(experiment.updatedAt, { format: "short" })}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
